import * as D from 'date-fns';
import { parseISO, subDays } from 'date-fns';
import Joi, { ValidationErrorItem } from 'joi';
import { unparse } from 'papaparse';
import { SsnDatabase } from '../ssn-db';
import { formatISODate, pick } from '../util';
import { CoverageTier, Relationship } from './enums';
import {
  dependentMemberSchema,
  dependentMemberSchemaV2,
  Member,
  primaryMemberSchema,
  ValidationContext as MemberValidationContext,
} from './member';

export interface MemberGroup {
  id: string;
  clientId: number;
  benefitPlanTimeFrame: string;
  benefitPlanName: string;
  benefitPlanId: number;
  coverageTier?: CoverageTier;
  organizationId: string;
  primary: Member;
  dependents: Member[];
  size: number;
  errors?: ValidationErrorItem[];
  warnings?: ValidationErrorItem[];
  invoiceGroup?: string;
  clientOrganizationId?: string;
  subOrganizationId?: string;
  reportingGroup?: string;
}

export interface EditMemberGroup {
  isNew?: boolean;
  id: string;
  clientId: number;
  benefitPlanTimeFrame: string;
  benefitPlanName: string;
  benefitPlanId: number;
  coverageTier: CoverageTier;
  organizationId: string;
  primary: Member;
  dependents: Member[];
  invoiceGroup?: string;
  clientOrganizationId?: string;
  subOrganizationId?: string;
  reportingGroup?: string;
}

// MemberGroup uses the same validation context as Member
export type ValidationContext = MemberValidationContext;

const CSV_COLUMNS = [
  'employeeID',
  'memberID',
  'relationshipCode',
  'firstName',
  'lastName',
  'birthDate',
  'email',
  'phoneNumber',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zipCode',
  'benefitPlanName',
  'coverageTier',
  'benefitStartDate',
  'benefitEndDate',
  'invoiceGroup',
];

const relationshipBitMap: Record<Relationship, number> = {
  [Relationship.SELF]: 1,
  [Relationship.SPOUSE]: 2,
  [Relationship.CHILD]: 4,
};

const coverageTierMaskMap: Record<CoverageTier, number> = {
  [CoverageTier.EMPLOYEE]: 1,
  [CoverageTier.EMPLOYEE_SPOUSE]: 1 | 2,
  [CoverageTier.EMPLOYEE_CHILDREN]: 1 | 4,
  [CoverageTier.FAMILY]: 1 | 2 | 4,
};

const dependentsRef = Joi.ref('dependents');

export const memberGroupSchemaBody = {
  id: Joi.required(),
  clientId: Joi.required(),
  organizationId: Joi.required(),
  benefitPlanTimeFrame: Joi.required().label('Plan Time Frame'),
  benefitPlanId: Joi.required().label('Plan'),
  benefitPlanName: Joi.required(),
  coverageTier: Joi.string()
    .required()
    // NOTE: .valid is not compatible with .custom https://github.com/hapijs/joi/issues/2526
    .custom((coverageTier: CoverageTier, helpers) => {
      const primaryBenefitEndDate = Joi.ref('/primary.benefitEndDate').resolve(
        helpers.original,
        helpers.state,
        helpers.prefs,
      );

      const primaryIsCancelTerminated = Joi.ref('/primary.isCancelTerminated').resolve(
        helpers.original,
        helpers.state,
        helpers.prefs,
      );

      const coverageTierMask = coverageTierMaskMap[coverageTier];

      if (!coverageTierMask) {
        return helpers.error('any.only', { valids: Object.keys(coverageTierMaskMap) });
      }

      if (primaryBenefitEndDate && D.isPast(D.parseISO(primaryBenefitEndDate))) {
        return coverageTier;
      }

      const dependents: Member[] = dependentsRef.resolve(undefined, helpers.state, helpers.prefs) ?? [];

      const relationshipBits = dependents.reduce(
        (b, d) =>
          (d.benefitEndDate && D.isPast(D.parseISO(d.benefitEndDate))) || d.isCancelTerminated
            ? b
            : b | relationshipBitMap[d.relationshipCode],
        1, // for the primary
      );

      if (coverageTierMask !== (relationshipBits & coverageTierMask) && !primaryIsCancelTerminated) {
        if (helpers.prefs.context?.isMembershipSingleSourceOfTruthInEligibility) {
          return helpers.error('coveragetier.overincluded');
        } else {
          helpers.warn('coveragetier.overincluded');
        }
      }

      return coverageTier;
    })
    .label('Coverage Tier'),
  invoiceGroup: Joi.string().empty('').label('Invoice Group'),
  primary: primaryMemberSchema.required(),
  dependents: Joi.array().items(dependentMemberSchema),
};

export const memberGroupSchema = Joi.object<EditMemberGroup>(memberGroupSchemaBody).messages({
  'coveragetier.overincluded': '{#label} is a higher tier than necessary for the current active dependents',
});

export const memberGroupSchemaV2 = Joi.object<EditMemberGroup>({
  ...memberGroupSchemaBody,
  dependents: Joi.array().items(dependentMemberSchemaV2),
  coverageTier: Joi.string()
    .required()
    // NOTE: .valid is not compatible with .custom https://github.com/hapijs/joi/issues/2526
    .custom((coverageTier: CoverageTier, helpers) => {
      const primaryBenefitEndDate = Joi.ref('/primary.benefitEndDate').resolve(
        helpers.original,
        helpers.state,
        helpers.prefs,
      );

      const primaryIsCancelTerminated = Joi.ref('/primary.isCancelTerminated').resolve(
        helpers.original,
        helpers.state,
        helpers.prefs,
      );

      const coverageTierMask = coverageTierMaskMap[coverageTier];

      if (!coverageTierMask) {
        return helpers.error('any.only', { valids: Object.keys(coverageTierMaskMap) });
      }

      const dependents: Member[] = dependentsRef.resolve(undefined, helpers.state, helpers.prefs) ?? [];

      const relationshipBits = dependents.reduce(
        (b, d) => (d.benefitEndDate || d.isCancelTerminated ? b : b | relationshipBitMap[d.relationshipCode]),
        1, // for the primary
      );

      const hasDependentsAndAllTerminated =
        dependents.length && dependents.every(d => d.benefitEndDate || d.isCancelTerminated);

      if (
        coverageTierMask !== (relationshipBits & coverageTierMask) &&
        !(hasDependentsAndAllTerminated && (primaryBenefitEndDate || primaryIsCancelTerminated))
      ) {
        return helpers.error('coveragetier.overincluded');
      }

      return coverageTier;
    })
    .label('Coverage Tier'),
}).messages({
  'coveragetier.overincluded':
    '{#label} is a higher tier than expected for the current set of dependents. The {#label} should reflect all dependents without a benefit end date.',
});

const benefitDateGraceDays = 30;

export function createValidationContext(
  ssnDB: SsnDatabase,
  planStartDate: string | undefined,
  planEndDate: string | undefined,
  primaryBenefitStartDate: string | undefined,
  primaryBenefitEndDate: string | undefined,
  isNew?: boolean | undefined,
  isMembershipSingleSourceOfTruthInEligibility?: boolean | undefined,
): ValidationContext {
  const dates = calculateValidationDates(
    planStartDate ? D.parseISO(planStartDate.split('T')[0]) : undefined,
    planEndDate ? D.parseISO(planEndDate.split('T')[0]) : undefined,
    primaryBenefitStartDate ? D.parseISO(primaryBenefitStartDate) : undefined,
    primaryBenefitEndDate ? D.parseISO(primaryBenefitEndDate) : undefined,
  );

  return {
    minBirthDate: formatISODate(dates.minBirthDate),
    maxBirthDate: formatISODate(dates.maxBirthDate),
    minBenefitStartDate: formatISODate(dates.minBenefitStartDate),
    maxBenefitStartDate: dates.maxBenefitStartDate ? formatISODate(dates.maxBenefitStartDate) : undefined,
    minBenefitEndDate: dates.minBenefitEndDate ? formatISODate(dates.minBenefitEndDate) : undefined,
    maxBenefitEndDate: dates.maxBenefitEndDate ? formatISODate(dates.maxBenefitEndDate) : undefined,
    maxDependentBenefitEndDate: formatISODate(dates.maxDependentBenefitEndDate),
    isSsnUnique: (ssn, relationshipCode) => ssnDB.isUnique(ssn, relationshipCode),
    isNew: isNew ?? false,
    primaryBenefitStartDate: dates.primaryBenefitStartDate ? formatISODate(dates.primaryBenefitStartDate) : undefined,
    primaryBenefitEndDate: dates.primaryBenefitEndDate ? formatISODate(dates.primaryBenefitEndDate) : undefined,
    isMembershipSingleSourceOfTruthInEligibility,
  };
}

function calculateValidationDates(
  planStartDate: Date | undefined,
  planEndDate: Date | undefined,
  primaryBenefitStartDate: Date | undefined,
  primaryBenefitEndDate: Date | undefined,
) {
  const now = Date.now();

  const graceDaysBehind = D.subDays(now, benefitDateGraceDays);
  const graceDaysAhead = D.addDays(now, benefitDateGraceDays);

  const result = {
    minBirthDate: D.subYears(now, 100),
    maxBirthDate: D.endOfDay(now),
    minBenefitStartDate: planStartDate ?? graceDaysBehind,
    maxBenefitStartDate: planEndDate,
    minBenefitEndDate: planStartDate,
    maxBenefitEndDate: planEndDate ?? undefined,
    maxDependentBenefitEndDate: graceDaysAhead,
    primaryBenefitStartDate,
    primaryBenefitEndDate,
  };

  if (planStartDate) {
    result.minBenefitStartDate = D.max([planStartDate, result.minBenefitStartDate]);
  }

  return result;
}

export async function writeGroupToCsv(memberGroups: MemberGroup[]): Promise<string> {
  const formattedGroups = memberGroups.flatMap(
    ({ benefitPlanName, coverageTier, invoiceGroup, primary, dependents }) => {
      const commonData = {
        externalEmployeeId: primary.externalEmployeeId,
        benefitPlanName,
        coverageTier,
        invoiceGroup,
      };

      const members = [formatMemberForCsv({ ...commonData, member: primary })];

      for (const dependent of dependents) {
        members.push(formatMemberForCsv({ ...commonData, member: dependent }));
      }

      return members;
    },
  );

  return unparse(formattedGroups, { columns: CSV_COLUMNS });
}

function formatMemberForCsv({
  member,
  externalEmployeeId,
  benefitPlanName,
  coverageTier,
  invoiceGroup,
}: {
  member: Member;
  externalEmployeeId: string;
  benefitPlanName: string;
  coverageTier?: CoverageTier;
  invoiceGroup?: string;
}) {
  return {
    ...pick(member, [
      'relationshipCode',
      'firstName',
      'lastName',
      'birthDate',
      'email',
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'zipCode',
      'benefitStartDate',
    ]),
    benefitEndDate: member.isCancelTerminated
      ? formatISODate(subDays(parseISO(member.benefitStartDate), 1))
      : member.benefitEndDate,
    phoneNumber: member.phone,
    employeeID: externalEmployeeId,
    memberID: member.ssn,
    benefitPlanName,
    coverageTier,
    invoiceGroup,
  };
}
